import React, { useState } from "react";
import { Container } from "../../../../components/common/HtmlTags/Container/Container";
import { Img } from "../../../../components/common/HtmlTags/Img/Img";
import { Span } from "../../../../components/common/HtmlTags/Span/Span";
import { Edit, Trash2 } from "lucide-react";
import "./ProfileAvatar.css";

type ProfileAvatarProps = {
  profileImg?: string;
  profileName?: string;
  avatarSize?: "small" | "medium" | "large";
  showActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
};

const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  profileImg,
  profileName,
  avatarSize = "small",
  showActions = false,
  onEdit,
  onDelete,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!profileImg && !profileName) return null;

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <Container
      data-testid="profile-avatar"
      className={`avatar-circle ${avatarSize} ${showActions ? "avatar-with-actions" : ""}`}
      onMouseEnter={() => showActions && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <>
        {profileImg ? (
          <Img
            src={profileImg}
            alt={`${profileName} profile`}
            className="avatar-cirle-img"
          />
        ) : (
          <Span>{profileName?.slice(0, 2)}</Span>
        )}
      </>
      {showActions && isHovered && (
        <Container className="avatar-actions-overlay">
          <Container
            className="avatar-action-button avatar-edit-button"
            role="button"
            aria-label="Edit profile photo"
            onClick={handleEditClick}
          >
            <Edit size={20} />
          </Container>
          {profileImg && (
            <Container
              className="avatar-action-button avatar-delete-button"
              role="button"
              aria-label="Delete profile photo"
              onClick={handleDeleteClick}
            >
              <Trash2 size={20} />
            </Container>
          )}
        </Container>
      )}
    </Container>
  );
};

export default ProfileAvatar;

/**
 * ProfileAvatar - A reusable component for displaying user profile avatars
 *
 * This component renders a circular avatar that can display either an image or text initials.
 * It supports three different size variants and gracefully handles cases where no image or name is provided.
 * If neither profileImg nor profileName is provided, the component returns null.
 *
 * @component
 * @param {ProfileAvatarProps} props - The component props
 * @returns {JSX.Element | null} The rendered ProfileAvatar component or null if no data provided
 *
 * @example
 * // With profile image
 * <ProfileAvatar
 *   profileImg="/path/to/image.jpg"
 *   profileName="John Doe"
 *   avatarSize="medium"
 * />
 *
 * @example
 * // With text initials only
 * <ProfileAvatar
 *   profileName="Jane Smith"
 *   avatarSize="large"
 * />
 *
 * @example
 * // Small avatar (default size)
 * <ProfileAvatar
 *   profileImg="/avatar.jpg"
 *   profileName="Bob Wilson"
 * />
 */
